import { useContext } from 'react';
import { useCompletionContractCheck } from '@common/hooks/completion-contract-check';
import { logEvent } from 'firebase/analytics';

import { useCompleteContractMutation } from '@/api/queries/contract';
import { specContractList } from '@/common/constants/contract';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { checkIfContractHasProductOfDay } from '@/common/lib/contract/check-if-contract-has-product-of-day';
import { getContractExtraStarPoints } from '@/common/lib/getContractExtaStarPoints';
import { playSound } from '@/common/lib/soundEffect';
import {
  type GetContractsResponseType,
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import { CompleteContractAnimationContext } from '@/components/Animations/CompleteContractAnimation/context';
import { ModalContext } from '@/components/Modals/Modal/config';
import { analytics } from '@/firebase';
import {
  removeContractById,
  setContractList,
} from '@/redux/reducers/contractReducer';
import { addToBalance, changeStorage } from '@/redux/reducers/userReducer';
import {
  selectActiveBlitzContractStep,
  selectSpecContractDataByKind,
} from '@/redux/selectors/contractSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { StepsContext } from '../Blitz/context';

interface UseCompleteSpecContractReturnType {
  onClick: () => void;
  isLoading: boolean;
  inactive: boolean;
  disabled: boolean;
}

const useCompleteSpecContact = (
  kind: SpecContractType | PaidSpecContractType,
): UseCompleteSpecContractReturnType => {
  const freeBlitzActiveStep = useAppSelector((state) =>
    selectActiveBlitzContractStep(state, 'Blitz'),
  );
  const paidBlitzActiveStep = useAppSelector((state) =>
    selectActiveBlitzContractStep(state, 'BlitzPlus'),
  );

  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const withVipStatus = useCheckVIPstatus();
  const contract = useAppSelector((state) =>
    selectSpecContractDataByKind(state, kind),
  );
  const productsOfDay = useAppSelector((state) => state.contract.productOfDay);
  const hasContractComplete = useCompletionContractCheck(contract?.data);
  const [completeContract, { isLoading }] = useCompleteContractMutation({
    fixedCacheKey: 'shared-complete-contract',
  });
  const { closeModal, setOverlayIsActive } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const {
    setReward,
    setContractId,
    contractListWrapperRef,
    setIsAnimationStart,
    lastCompleteContractHasProductOfDayRef,
  } = useContext(CompleteContractAnimationContext);
  const { swiper } = useContext(StepsContext);

  const isSpecContract: boolean = (specContractList as string[]).includes(
    contract?.type || '',
  );
  const isTutorial: boolean = false;

  const withAdditionalReward = withVipStatus && !isSpecContract && !isTutorial;
  const cashReward = withAdditionalReward
    ? (contract?.cash || 0) * 1.1
    : contract?.cash || 0;

  const sendAnalitics = (): void => {
    if (!analytics || !contract) return;
    Object.entries(contract.data).forEach(([name, value]) => {
      if (!analytics) return;
      logEvent(analytics, 'currency_spent', {
        currency_type: name,
        amount: value.amount,
        reason: 'contract_completion',
        contract_id: contract.id,
      });
    });
    logEvent(analytics, 'currency_earned', {
      currency_type: 'cash',
      amount: contract.cash,
      source: 'contract_completion',
      contract_id: contract.id,
    });
    logEvent(analytics, 'currency_earned', {
      currency_type: 'contract_point',
      amount: contract.point,
      source: 'contract_completion',
      contract_id: contract.id,
    });
  };

  const preparationFoRequest = (): void => {
    if (!contract || isFirstVisit) return;
    setOverlayIsActive(true);
    setReward({
      cash: cashReward,
      point:
        contract.point +
        getContractExtraStarPoints(contract, productsOfDay || []),
      dice: contract.dice || 0,
      ...contract.lotteryTickets,
    });
    setContractId(contract.id);
  };

  const updateStorage = (): void => {
    if (!contract) return;
    const storageData: Record<string, number> = {};
    for (const [key, value] of Object.entries(contract.data)) {
      storageData[key] = value.amount;
    }
    dispatch(changeStorage(storageData));
  };

  const successContractComplete = (contractHasProductOfDay: boolean): void => {
    if (contractListWrapperRef.current) {
      contractListWrapperRef.current.scroll({
        left: 0,
        top: 0,
        behavior: 'smooth',
      });
    }

    if (!contract) return;
    updateStorage();
    dispatch(
      addToBalance({
        cash: cashReward,
      }),
    );

    lastCompleteContractHasProductOfDayRef.current = contractHasProductOfDay;
    dispatch(removeContractById(contract.id));

    if (!isFirstVisit) setIsAnimationStart(true);

    closeModal();
  };

  const toNextStep = (response: GetContractsResponseType): void => {
    updateStorage();
    dispatch(setContractList(response.contracts));
    swiper.current?.slideNext();
  };

  const onClick = (): void => {
    if (!hasContractComplete || !contract) return;
    playSound('buttons/switch');
    const isLastStep =
      (kind === 'Blitz' && freeBlitzActiveStep === 4) ||
      (kind === 'BlitzPlus' && paidBlitzActiveStep === 4) ||
      (kind !== 'Blitz' && kind !== 'BlitzPlus');

    if (isLastStep) {
      preparationFoRequest();
    }
    const contractHasProductOfDay = checkIfContractHasProductOfDay(
      productsOfDay,
      contract.data,
    );
    completeContract({
      contractId: contract.id,
    })
      .unwrap()
      .then((response) =>
        isLastStep
          ? successContractComplete(contractHasProductOfDay)
          : toNextStep(response),
      );
    sendAnalitics();
  };

  return {
    onClick,
    isLoading,
    inactive: !hasContractComplete,
    disabled: !hasContractComplete || isLoading,
  };
};

export { useCompleteSpecContact };
